import { useCallback, useEffect, useState } from 'preact/hooks';

export interface PresenceState {
  /** True while the element should be in the DOM (open OR exiting). */
  mounted: boolean;
  /** True while the exit animation is running. Apply exit classes when true. */
  exiting: boolean;
  /** Hook this to the panel's onAnimationEnd to finalize the unmount. */
  onExitEnd: () => void;
}

/**
 * Keeps a component mounted long enough to play an exit animation.
 *
 * Usage:
 *   const { mounted, exiting, onExitEnd } = usePresence(open);
 *   if (!mounted) return null;
 *   return <div onAnimationEnd={onExitEnd} className={exiting ? 'animate-fade-out' : 'animate-fade-in'}>...</div>;
 */
export function usePresence(open: boolean): PresenceState {
  const [mounted, setMounted] = useState(open);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setExiting(false);
    } else if (mounted) {
      setExiting(true);
    }
  }, [open, mounted]);

  const onExitEnd = useCallback(() => {
    if (!exiting) return;
    setMounted(false);
    setExiting(false);
  }, [exiting]);

  return { mounted, exiting, onExitEnd };
}
